// ⚠️ MIRROR COPY — keep byte-for-byte identical with the twin in the other repo:
//   - portal-frontend: src/lib/portal-utils.ts        (live guest runtime)
//   - main-frontend:   src/lib/utils/portal-utils.ts  (portal builder preview)
// The guest portal has no shared rendering package, so the builder preview and
// the runtime are hand-maintained twins (same convention as the
// website-builder editor↔runtime split). Change one → change the other
// identically, or the preview silently drifts from what guests actually see.

import type {
  PortalConfig,
  PortalHeaderFontWeight,
  PortalHeroImagePlacement,
  PortalTextAlign,
  PortalTextBlock,
} from "@/types/PortalConfig";

export const DEFAULT_CARD_BORDER_RADIUS = "16px";

/**
 * Brand-neutral baseline applied when an event has no portal config (or leaves
 * individual fields unset). This is the SINGLE source of defaults shared by the
 * builder preview and the live runtime — render both through withPortalDefaults
 * so "what the admin sees in the builder" is exactly "what the guest sees",
 * deterministically and independent of the guest's OS dark-mode. (Before this,
 * the runtime fell through to prefers-color-scheme dark tokens for unset
 * fields, so an unconfigured portal rendered black for dark-mode guests while
 * the builder showed light.)
 */
export const DEFAULT_PORTAL_CONFIG: PortalConfig = {
  backgroundColor: "#f5f5f5",
  cardBackgroundColor: "#ffffff",
  cardBorderColor: "#e5e5e5",
  // No default accentColor: the download icon falls back to the muted icon
  // colour (like the paperclip/file icons) unless the admin picks an accent.
  textColor: "#171717",
  secondaryTextColor: "#737373",
  fontFamily: "Inter, system-ui, sans-serif",
  cardBorderRadius: "16px",
  // The header enum belongs HERE and not only in the resolver below:
  // withPortalDefaults reads `null` as "unset ⇒ default", and the cockpit's
  // dirty-compare runs both sides through it. A field the defaults lack is a
  // field the compare sees on one side only — the M17 / PB-WBO-1 shape that
  // leaves Save armed forever.
  headerFontWeight: "semibold",
};

/**
 * Merge a fetched config onto DEFAULT_PORTAL_CONFIG, treating `null` /
 * `undefined` fields as "unset → use the default" (the portal API serialises
 * every unset field as explicit `null`, so a naive spread would clobber the
 * defaults back to null).
 */
export function withPortalDefaults(config?: PortalConfig | null): PortalConfig {
  const merged: PortalConfig = { ...DEFAULT_PORTAL_CONFIG };
  if (!config) return merged;
  for (const key of Object.keys(config) as (keyof PortalConfig)[]) {
    const value = config[key];
    if (value !== null && value !== undefined) {
      (merged as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}

/**
 * Nested dot-path lookup over the variable context, mirroring the email
 * builder's `getNestedValue` (variable-replacer.ts) so custom fields land
 * the same way: `additionalFields.<name>`.
 */
export function getPortalVarValue(
  ctx: Record<string, unknown>,
  path: string,
): unknown {
  return path.split(".").reduce<unknown>((cur, key) => {
    if (cur !== null && cur !== undefined && typeof cur === "object") {
      return (cur as Record<string, unknown>)[key];
    }
    return undefined;
  }, ctx);
}

/**
 * Replace `{{var}}` placeholders in a portal text string with values from
 * `ctx`. Mirrors the email builder's `replaceVariablesInText`: unknown / null
 * variables render to an empty string (never the literal `{{key}}`).
 *
 * <p>The legacy single-brace `{guestName}` form is still honoured so portals
 * authored before the `{{double-brace}}` unification keep rendering. The picker
 * only ever inserts the double-brace form now.
 *
 * <p>Variables outside the set the portal runtime actually produces
 * (appointment / seating / URLs — there is no such data in a documents portal)
 * are simply absent from `ctx` and therefore render empty in BOTH the builder
 * preview and the live runtime, so the preview never promises a value the guest
 * won't see.
 */
export function resolvePortalVars(
  text: string,
  ctx: Record<string, unknown>,
): string {
  if (!text) return text;
  // Double braces FIRST: the legacy single-brace pass would otherwise eat the
  // inner `{guestName}` of `{{guestName}}` and leave `{Maja}` on the page.
  const unified = text.replace(/\{\{([a-zA-Z0-9_.]+)\}\}/g, (_m, key: string) => {
    const v = getPortalVarValue(ctx, key);
    return v !== undefined && v !== null ? String(v) : "";
  });
  return unified.replace(/\{guestName\}/g, () => {
    const v = ctx["guestName"];
    return v !== undefined && v !== null ? String(v) : "";
  });
}

export function getCustomText(
  texts: Record<string, string> | undefined,
  locale: string,
  fallback: string,
  defaultLocale?: string,
): string {
  if (!texts) return fallback;
  // CLEARED is not the same as ABSENT, and the difference decides which
  // language the guest reads:
  //
  //   absent  — this language was never configured. Borrowing the event's
  //             default language (or any other configured one) is better than
  //             the generic bundled string. Unchanged house rule.
  //   cleared — the operator emptied THIS field on purpose, and the input's
  //             placeholder promises the bundled default back. Falling through
  //             to another language would put a foreign word on the page: an
  //             emptied German privacy label used to serve the English one,
  //             which is the exact defect this whole wave exists to remove.
  //
  // So: the first candidate whose KEY EXISTS decides. Empty ⇒ the fallback.
  const chain = defaultLocale
    ? [locale, locale.split("-")[0]!, defaultLocale, defaultLocale.split("-")[0]!]
    : [locale, locale.split("-")[0]!];
  // `typeof`, not `!== undefined`: HEAD never touched the value, so a legacy
  // or hand-PUT non-string (`null`) was inert. Trimming one would throw —
  // a render crash in the SPA, a 500 on the edge — and Java's `@Size` does
  // not reject null. Anything that is not a string counts as absent.
  for (const key of chain) {
    const value = texts[key];
    if (typeof value !== "string") continue;
    return value.trim().length > 0 ? value : fallback;
  }
  // Nothing configured for the guest's language or the event's default: take
  // the first language that actually has text.
  return Object.values(texts).find((v) => typeof v === "string" && v.trim().length > 0) ?? fallback;
}

// ─── Header block: title weight ──────────────────────────────────────────────
//
// Tailwind finds class names by scanning the SOURCE with a regex. A class
// assembled at runtime — `font-${weight}`, `"font-" + weight` — is invisible to
// it: the class lands on the element, the RULE never lands in the stylesheet,
// and the setting does nothing in production while every jsdom assertion about
// `className` still passes. So the enum resolves through a lookup map whose
// values are literal, and that map is the only place the classes are written.

/** Title weight. `font-semibold` is the class the H1 has always carried. */
export const HEADER_WEIGHT_CLASS: Record<PortalHeaderFontWeight, string> = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

/** Today's rendering — the weight the H1 has always been drawn at, and the
 *  value an untouched portal must resolve to. */
export const DEFAULT_HEADER_FONT_WEIGHT: PortalHeaderFontWeight = "semibold";

/**
 * Config value → a value the map actually carries. Anything else (unset, null,
 * a newer client's spelling, a hand-PUT string) falls back to today's
 * rendering rather than indexing the map with it — `map[value]` would hand
 * `undefined` to a className, and `value in map` would say yes to
 * `constructor`.
 */
export function resolveHeaderFontWeight(
  value: string | null | undefined,
): PortalHeaderFontWeight {
  return typeof value === "string" && Object.hasOwn(HEADER_WEIGHT_CLASS, value)
    ? (value as PortalHeaderFontWeight)
    : DEFAULT_HEADER_FONT_WEIGHT;
}

// ─── Text blocks: per-block alignment ────────────────────────────────────────
//
// Same Tailwind rule as the title weight above, and the same shape for the
// same reason: `text-${align}` compiles, lands on the element and renders
// NOTHING, because the rule was never written into the stylesheet. Only the
// literal maps below may spell these classes.
//
// Unset is NOT "left". Each block is drawn today by what it inherits — the
// header block from the page, the ticket card and the footer from their own
// `text-center` — so an unset block gets no class at all and every live
// portal keeps the exact markup it has now. `PORTAL_TEXT_BLOCK_DEFAULT_ALIGN`
// only describes that, for the builder's controls.

/** Alignment → the literal Tailwind class for a line of text. */
export const TEXT_ALIGN_CLASS: Record<PortalTextAlign, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

/**
 * The same three for a block-level BOX rather than text — the hero icon, the
 * empty-state paperclip. Tailwind's preflight gives `img` / `svg`
 * `display:block`, and a block box with a width of its own does not move with
 * `text-*`: its alignment is its margins.
 */
export const BLOCK_ALIGN_CLASS: Record<PortalTextAlign, string> = {
  left: "mr-auto",
  center: "mx-auto",
  right: "ml-auto",
};

/** Every block an operator can align, in the order the builder lists them.
 *  Identical to the server's allowlist — an unknown key is dropped there and
 *  dropped again here, because the client turns it into a class name. */
export const PORTAL_TEXT_BLOCKS: readonly PortalTextBlock[] = [
  "eyebrow",
  "pageTitle",
  "subtitle",
  "title",
  "hint",
  "emptyState",
  "footer",
] as const;

/**
 * What each block is drawn as TODAY with nothing configured. The renderer
 * never reads this — an unset block gets no class — but the builder's
 * dropdowns open on it, so the control shows the operator what is actually on
 * the page instead of a blank, or a "left" that is a lie for three of them.
 */
export const PORTAL_TEXT_BLOCK_DEFAULT_ALIGN: Record<PortalTextBlock, PortalTextAlign> = {
  eyebrow: "left",
  pageTitle: "left",
  subtitle: "left",
  title: "left",
  hint: "center",
  emptyState: "center",
  footer: "center",
};

/**
 * The alignment configured for one block, or `null` for "nothing chosen —
 * leave the markup alone".
 *
 * `Object.hasOwn` on both the map and the vocabulary: `map[key]` answers for
 * `constructor`, and that answer would be indexed straight into a className.
 */
export function resolvePortalTextAlign(
  textAlign: Record<string, string> | null | undefined,
  block: PortalTextBlock,
): PortalTextAlign | null {
  if (!textAlign || typeof textAlign !== "object") return null;
  if (!Object.hasOwn(textAlign, block)) return null;
  const value = textAlign[block];
  return typeof value === "string" && Object.hasOwn(TEXT_ALIGN_CLASS, value)
    ? (value as PortalTextAlign)
    : null;
}

/**
 * `base` plus the block's alignment class — and EXACTLY `base`, with no
 * trailing space, when the block is unset. The whole-`class`-attribute pins
 * on both twins compare strings, and so does a diff of a live portal.
 */
export function withPortalAlign(
  base: string,
  textAlign: Record<string, string> | null | undefined,
  block: PortalTextBlock,
): string {
  const align = resolvePortalTextAlign(textAlign, block);
  return align ? `${base} ${TEXT_ALIGN_CLASS[align]}` : base;
}

/**
 * The same for a block-level box. `fallback` is the margin class the element
 * carries today (the empty-state icon's `mx-auto`; the hero icon has none) and
 * is REPLACED, never joined: `mx-auto mr-auto` is decided by the order of two
 * rules in the stylesheet, not by the operator.
 */
export function withPortalBoxAlign(
  base: string,
  textAlign: Record<string, string> | null | undefined,
  block: PortalTextBlock,
  fallback = "",
): string {
  const align = resolvePortalTextAlign(textAlign, block);
  const cls = align ? BLOCK_ALIGN_CLASS[align] : fallback;
  return cls ? `${cls} ${base}` : base;
}

// ─── Header image ────────────────────────────────────────────────────────────

/** Both placements, in the order the builder offers them. */
export const PORTAL_HERO_IMAGE_PLACEMENTS: readonly PortalHeroImagePlacement[] = [
  "above",
  "below",
] as const;

/** Unset reads as `above`. Nothing renders differently for a portal without a
 *  picture, so this is only the shape the builder offers first. */
export const DEFAULT_HERO_IMAGE_PLACEMENT: PortalHeroImagePlacement = "above";

/** Anything that is not exactly `below` is `above` — a newer client's
 *  spelling or a hand-PUT value must not fall through to `undefined`. */
export function resolveHeroImagePlacement(
  value: string | null | undefined,
): PortalHeroImagePlacement {
  return value === "below" ? "below" : DEFAULT_HERO_IMAGE_PLACEMENT;
}

/** Narrowest and widest header picture a band may be reserved for — the same
 *  0.2 … 5.0 the DTO accepts, so the client never reserves a shape a save
 *  would have refused. */
export const HERO_IMAGE_ASPECT_MIN = 0.2;
export const HERO_IMAGE_ASPECT_MAX = 5;

/**
 * The picture's width ÷ height, or `null` when it is not known.
 *
 * `null` is the point of this helper, not an oversight. The ticket banner
 * taught the lesson twice: a GUESSED ratio cropped 18-82 % off real
 * operators' pictures, and a band reserved for bytes that never arrived left
 * a permanent 199-px void that pushed the QR further from the fold than no
 * reservation at all. So absent, unparsable or out-of-range reserves NO
 * height — the picture is still drawn, at `h-auto`.
 */
export function resolveHeroImageAspect(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value >= HERO_IMAGE_ASPECT_MIN && value <= HERO_IMAGE_ASPECT_MAX ? value : null;
}

/** The header picture with its band reserved (`aspectRatio` is an inline
 *  style: `aspect-[${w}/${h}]` is exactly the arbitrary value Tailwind cannot
 *  read out of the source).
 *
 *  `max-h-[60vh]` is the second half of the ticket banner's lesson: reserve the
 *  right shape, but never let the header BE the page. Uncapped, the narrowest
 *  ratio the DTO accepts (0.2) reserves 1875 px on a 375-px phone — five
 *  screens of picture above the eyebrow, and `above` is the default placement.
 *  Capped, the replaced-element min/max rules shrink the used width with the
 *  height, so `mx-auto` keeps the picture centred instead of hugging the left.
 *  It has to be a class: the ratio next to it is an INLINE style, which no
 *  operator `customCss` rule can reach without `!important`. */
export const HERO_IMAGE_BAND_CLASS = "block w-full max-h-[60vh] mx-auto object-cover mb-6";
/** …and without one: no reserved height, nothing cropped — but the same cap.
 *  This is the lane a too-tall pick actually LANDS in: 400x3000 measures
 *  0.1333, which is out of the DTO's range, so no ratio is stored at all and
 *  the picture would otherwise draw at its own 2812 px. */
export const HERO_IMAGE_AUTO_CLASS = "block w-full h-auto max-h-[60vh] mx-auto mb-6";

export function buildPortalStyle(config?: PortalConfig): React.CSSProperties {
  if (!config) return {};
  const style: React.CSSProperties = {};
  if (config.backgroundColor) style.backgroundColor = config.backgroundColor;
  if (config.textColor) style.color = config.textColor;
  if (config.fontFamily) style.fontFamily = config.fontFamily;
  return style;
}

export function buildCardStyle(config?: PortalConfig): React.CSSProperties {
  if (!config) return { borderRadius: DEFAULT_CARD_BORDER_RADIUS };
  const style: React.CSSProperties = {
    borderRadius: config.cardBorderRadius ?? DEFAULT_CARD_BORDER_RADIUS,
  };
  if (config.cardBackgroundColor) style.backgroundColor = config.cardBackgroundColor;
  if (config.cardBorderColor) style.borderColor = config.cardBorderColor;
  return style;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Decode a JWT's `exp` claim without verifying signature (signature
 * already verified server-side). Returns null on any decode failure —
 * caller treats null as "no expiry banner".
 */
export function decodeTokenExp(token: string): Date | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    if (typeof payload.exp !== "number") return null;
    return new Date(payload.exp * 1000);
  } catch {
    return null;
  }
}

/**
 * Reject any URL that's not https://. Logo / icon URLs are admin-supplied
 * and rendered into <img> / <link> — defense-in-depth even though backend
 * also caps length.
 */
/**
 * Sanitize an admin-supplied external link (privacy policy) before it is
 * bound into an <a href>. Only http(s) URLs pass — anything else
 * (javascript:, data:, relative junk) returns null so the link is simply
 * not rendered. Second wall behind the worker DTO's sanitizeHttpUrl (L12).
 */
export function sanitizeExternalUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    if (!parsed.hostname || parsed.hostname.length === 0) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

export function sanitizeImageUrl(url: string | undefined | null): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return null;
    if (!parsed.hostname || parsed.hostname.length === 0) return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

// ─── Ticket (QR + wallet passes + PDF) ────────────────────────────────────────
//
// Shared by the guest runtime (portal-frontend TicketSection) and the builder
// preview (main-frontend PortalPreview). Pure helpers only — no fetching, no
// device sniffing (those stay runtime-only).

export type TicketDropKind = "apple" | "google" | "pdf";
export type TicketPlatform = "ios" | "android" | "other";
export type TicketUnavailableKey =
  | "unavailableApple"
  | "unavailableGoogle"
  | "unavailablePdf"
  | "ineligible";

/** Defaults for the nested `ticket` block. `enabled` false = no section. */
export const DEFAULT_TICKET_CONFIG: Required<
  Pick<NonNullable<PortalConfig["ticket"]>, "enabled" | "showQr" | "showCompanions">
> = {
  enabled: false,
  showQr: true,
  // True is what every event had before the flag, and the portal reads an
  // absent value the same way — so the default here and the default there
  // cannot drift apart.
  showCompanions: true,
};

/** QR module grid is drawn at this CSS size inside a white 12px-padded panel. */
export const TICKET_QR_SIZE = 232;
/** Quiet zone in modules, baked into the SVG viewBox on both twins. */
export const TICKET_QR_QUIET = 4;

/**
 * The centre logo (`ticket.qrLogoUrl`), as a fraction of the symbol's side.
 *
 * The code is encoded at ECC H ONLY when a logo is configured (M otherwise,
 * so an untouched portal keeps today's 29-module raster). At H the symbol
 * recovers from 30 % damage; the logo box below removes ~3.6 % of it, and it
 * sits in the middle where no finder, timing or alignment pattern lives —
 * the badge that once killed the code at the door covered 13 % ACROSS the
 * timing column at ECC M (TicketCard.tsx header). The e2e decode pin
 * (PT-5 / PT-23, jsQR on a screenshot) is what keeps this claim honest.
 */
export const TICKET_QR_LOGO_FRACTION = 0.2;

/**
 * Logo side in modules for a symbol of `modules` modules: the fraction,
 * rounded, forced ODD so the box centres on the (odd-sized) module grid with
 * an integer offset. 37 → 7, 29 → 5.
 */
export function ticketQrLogoModules(modules: number): number {
  const rounded = Math.max(1, Math.round(modules * TICKET_QR_LOGO_FRACTION));
  return rounded % 2 === 0 ? rounded - 1 : rounded;
}

/**
 * Where the logo box sits, in MODULE coordinates of the data grid (no quiet
 * zone): `[offset, offset + size)` on both axes. Both twins draw the
 * `<image>` at `offset + TICKET_QR_QUIET` in viewBox units and blank exactly
 * these modules — the runtime by skipping them in its path, the cockpit via
 * qrcode.react's `excavate`, whose rectangle is `floor(x) … ceil(w)` of the
 * image box and therefore lands on this very square ONLY when the image is
 * `size · TICKET_QR_SIZE / total` wide and x/y are left to the library.
 */
export function ticketQrLogoBox(modules: number): { size: number; offset: number } {
  const size = ticketQrLogoModules(modules);
  return { size, offset: (modules - size) / 2 };
}

/** Scrim over the card background picture, in percent — what an absent or
 *  out-of-band `ticket.cardBackgroundOverlay` reads as. The cockpit writes
 *  `null` at this value so a defaulted slider never arms Save. */
export const TICKET_CARD_OVERLAY_DEFAULT = 60;

/** Integer 0–100, else the default. Never null, never raw: the value becomes
 *  an alpha in a CSS gradient, so a stray string or 60.5 must not reach it. */
export function resolveTicketCardOverlay(value: unknown): number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 100
    ? value
    : TICKET_CARD_OVERLAY_DEFAULT;
}

/**
 * The ticket card's surface: `buildCardStyle` plus, when the operator set a
 * background picture, that picture UNDER a scrim in the card colour.
 *
 * Painted as ONE `background-image` on the card element itself — a gradient
 * layer over the picture — rather than an extra `<img>` or overlay node:
 * nothing new is drawn over the QR (the door-scanner probe in e2e PT-5 walks
 * every descendant of the card), the byte-identical `TicketCard` twin stays
 * untouched, and a picture that never arrives simply leaves the plain card
 * colour behind, no `onError` needed. The URL is already https-checked by
 * the caller (sanitizeImageUrl) and quoted with JSON.stringify so a `"` or
 * `)` in it cannot break out of `url()`.
 */
export function buildTicketCardStyle(
  config: PortalConfig | undefined,
  background: { imageUrl: string | null; overlay: number },
): React.CSSProperties {
  const style = buildCardStyle(config);
  if (!background.imageUrl) return style;
  const rgb = parsePortalColor(config?.cardBackgroundColor) ??
    parsePortalColor(DEFAULT_PORTAL_CONFIG.cardBackgroundColor) ?? { r: 255, g: 255, b: 255, a: 1 };
  const alpha = resolveTicketCardOverlay(background.overlay) / 100;
  const scrim = `rgba(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)}, ${alpha})`;
  return {
    ...style,
    backgroundImage: `linear-gradient(${scrim}, ${scrim}), url(${JSON.stringify(background.imageUrl)})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

/**
 * Canonical form of the nested ticket config: the API echoes every unset
 * field as `null`; strip those so a fresh `{ticket:null}` echo and an unset
 * ticket compare equal (same reasoning as withPortalDefaults above).
 */
export function normalizeTicketConfig(
  ticket: PortalConfig["ticket"] | undefined,
): NonNullable<PortalConfig["ticket"]> | undefined {
  if (!ticket) return undefined;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(ticket)) {
    const value = (ticket as Record<string, unknown>)[key];
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out as NonNullable<PortalConfig["ticket"]>;
}

/**
 * Perceived-luminance test for the card background, used to pick the outline
 * variant of the Apple badge on dark cards. Accepts #rgb/#rrggbb/#rrggbbaa;
 * anything unparsable counts as light (the default card is white).
 */
export function isDarkColor(hex: string | undefined | null): boolean {
  if (!hex) return false;
  const h = expandPortalHex(hex);
  if (!h) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Rec. 601 luma; 0.5 threshold matches what the eye calls "dark" for UI.
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}

/** `#rgb` / `#rrggbb` / `#rrggbbaa` → the six hex digits, else null. */
function expandPortalHex(hex: string | undefined | null): string | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec((hex ?? "").trim());
  if (!m) return null;
  const h = m[1]!;
  return h.length === 3 ? h.split("").map((c) => c + c).join("") : h.slice(0, 6);
}

type PortalRgba = { r: number; g: number; b: number; a: number };

/**
 * Every colour shape that can reach the portal's own chrome, as channels:
 * `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa`, `rgb(...)` and `rgba(...)` (comma
 * or space separated, numbers or percentages). Anything else → null.
 *
 * Wider than `expandPortalHex` on purpose. The stored value is only gated by
 * `^#[0-9a-f]{3,8}$` (portal-backend portal-boot-dto.ts, PortalPage.tsx, and
 * Java's `@Pattern` on PortalConfigDTO), which admits the 4-digit `#rgba`
 * form, and the cockpit's colour control emits `rgba(...)` below full
 * opacity — the builder preview paints that string verbatim.
 */
function parsePortalColor(value: string | undefined | null): PortalRgba | null {
  const raw = (value ?? "").trim();
  const hex = /^#([0-9a-f]+)$/i.exec(raw);
  if (hex) {
    const h = hex[1]!;
    const pairs =
      h.length === 3 || h.length === 4
        ? h.split("").map((c) => c + c)
        : h.length === 6 || h.length === 8
          ? (h.match(/.{2}/g) as string[])
          : null;
    if (!pairs) return null;
    const v = pairs.map((p) => parseInt(p, 16));
    return { r: v[0]!, g: v[1]!, b: v[2]!, a: v[3] === undefined ? 1 : v[3] / 255 };
  }
  const fn = /^rgba?\(([^)]*)\)$/i.exec(raw);
  if (!fn) return null;
  const parts = fn[1]!.split(/[,/\s]+/).filter((p) => p.length > 0);
  if (parts.length < 3 || parts.length > 4) return null;
  const num = (p: string, scale: number) => {
    const n = p.endsWith("%") ? (Number(p.slice(0, -1)) / 100) * scale : Number(p);
    return Number.isFinite(n) ? Math.min(scale, Math.max(0, n)) : null;
  };
  const r = num(parts[0]!, 255);
  const g = num(parts[1]!, 255);
  const b = num(parts[2]!, 255);
  const a = parts.length === 4 ? num(parts[3]!, 1) : 1;
  if (r === null || g === null || b === null || a === null) return null;
  return { r, g, b, a };
}

function toPortalHex({ r, g, b }: PortalRgba): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * What the guest's eye actually meets where an operator colour is painted:
 * an OPAQUE `#rrggbb`.
 *
 * The portal's chrome sits on the SPA's own page — `body { background:
 * #f5f5f5 }` (portal-frontend styles/index.css), the same value as
 * DEFAULT_PORTAL_CONFIG.backgroundColor — so:
 *   - a translucent colour (`#8888`, `#00000010`, `rgba(…, .5)`) is composited
 *     over that page. Measuring it raw is how `#00000010` read as pure BLACK
 *     and got the light ink on an effectively #e6e6e6 screen: 1.09:1;
 *   - a value neither we nor the browser can read (`#12345` passes the
 *     `{3,8}` gates and is not a CSS colour) leaves the element with no
 *     background of its own, so the page shows through. Returning the page
 *     colour there is not a guess — it is what is on screen.
 * The builder preview draws its loading state inside the cockpit rather than
 * on that page, so for a translucent colour its composite is approximate;
 * the ink it picks is the guest's, which is the point of the preview.
 */
export function flattenOnPortalPage(color: string | undefined | null): string {
  const page = DEFAULT_PORTAL_CONFIG.backgroundColor ?? "#f5f5f5";
  const c = parsePortalColor(color);
  if (!c) return page;
  if (c.a >= 1) return toPortalHex(c);
  const under = parsePortalColor(page) ?? { r: 245, g: 245, b: 245, a: 1 };
  return toPortalHex({
    r: c.r * c.a + under.r * (1 - c.a),
    g: c.g * c.a + under.g * (1 - c.a),
    b: c.b * c.a + under.b * (1 - c.a),
    a: 1,
  });
}

/**
 * WCAG 2.1 relative luminance (sRGB-linearized) of a colour as painted.
 *
 * Alpha is NOT part of the formula — a translucent colour has to be flattened
 * onto whatever is behind it FIRST (`flattenOnPortalPage`), which is what
 * `pickSpinnerInk` does; handing this function `#00000010` and reading the
 * answer as "black" is the bug, not the contract. Unparsable reads as white,
 * the same way an unusable config value falls back to the SPA's light default.
 */
export function relativeLuminance(color: string | undefined | null): number {
  const c = parsePortalColor(color);
  if (!c) return 1;
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
}

/** WCAG 2.1 contrast ratio (1 … 21), order-independent. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return la >= lb ? (la + 0.05) / (lb + 0.05) : (lb + 0.05) / (la + 0.05);
}

/** WCAG 1.4.11 — what a non-text indicator (the spinner) owes its background. */
export const MIN_INDICATOR_CONTRAST = 3;

/** The muted token the portal writes everywhere else (`--portal-fg-muted`). */
export const SPINNER_INK_MUTED = DEFAULT_PORTAL_CONFIG.secondaryTextColor ?? "#737373";
/** Ink for backgrounds the muted grey cannot serve, dark half … */
export const SPINNER_INK_LIGHT = "#e5e5e5";
/** … and light half. Paired with the light ink so that the worst background —
 *  the one where both measure the same — still clears 3:1 (their luminance
 *  ratio is 10:1, and the floor is its square root, 3.17:1). Widening either
 *  ink is fine; narrowing the pair re-opens the gap. */
export const SPINNER_INK_DARK = "#333333";

/**
 * The loading spinner's colour for an operator page background, chosen by
 * MEASURED contrast.
 *
 * This used to be a luma flip (`isDarkColor(bg) ? light : muted`). The muted
 * default's own luma is 0.451 and the flip trips at 0.5, so every background
 * in between was called "light" and got a dark grey on a light-ish ground:
 * #808080 measured 1.20:1, #999999 1.66:1 — a blank coloured screen for the
 * whole data fetch on roughly a third of grey brand colours. The muted grey
 * is kept where it still reads as muted (a background lighter than itself,
 * clearing 3:1 — the SPA's own #f5f5f5 among them); otherwise the ink is
 * whichever of the two measures better against this exact background.
 *
 * "This exact background" means the flattened one: `#8888` is a stored,
 * wired, painted colour that reads #bbbbbb on screen, and `#00000010` is a
 * 6 % wash, not black. Both used to be measured somewhere the guest never
 * looks (2.58:1 and 1.09:1 respectively).
 */
/**
 * Loading-screen timing (the pre-data screen, PortalPage.tsx LoadingScreen;
 * the cockpit's "page loading" preview has no network and uses none of
 * these — they live in the twin so both sides read ONE definition).
 */
/** How long the loading screen stays once the operator's animation has
 *  painted, even if the data lands earlier: one turn of a 1.44 s loop. A
 *  screen that never painted the animation (spinner path) is not held. */
export const LOADING_HOLD_MS = 1500;
/** While `/boot` is still unanswered the screen stays blank this long before
 *  the spinner appears — a fast boot with an animation never flashes it. */
export const SPINNER_GRACE_MS = 400;
/** With an animation configured the spinner is suppressed while its bytes
 *  load, but not for ever: after this the spinner is the fallback so a
 *  slow network never leaves the guest on a bare coloured screen. */
export const SPINNER_CAP_MS = 6000;

export function pickSpinnerInk(background: string | undefined | null): string {
  // Measure where the ink LANDS, not what the config says: translucent and
  // unreadable values are flattened onto the page first (see the helper).
  const bg = flattenOnPortalPage(background);
  if (
    relativeLuminance(bg) > relativeLuminance(SPINNER_INK_MUTED) &&
    contrastRatio(SPINNER_INK_MUTED, bg) >= MIN_INDICATOR_CONTRAST
  ) {
    return SPINNER_INK_MUTED;
  }
  return contrastRatio(SPINNER_INK_LIGHT, bg) >= contrastRatio(SPINNER_INK_DARK, bg)
    ? SPINNER_INK_LIGHT
    : SPINNER_INK_DARK;
}

/**
 * Device-aware action order. iPhone owners want Apple first and there is no
 * Google Wallet app with a pkpass equivalent on iOS beyond the web save, so
 * Google trails the PDF there. Android has no Apple Wallet at all → hidden.
 * Desktop shows everything, PDF last.
 */
export function ticketActionOrder(platform: TicketPlatform): TicketDropKind[] {
  switch (platform) {
    case "ios":
      return ["apple", "pdf", "google"];
    case "android":
      return ["google", "pdf"];
    default:
      return ["apple", "google", "pdf"];
  }
}

/**
 * Map a backend drop reason (e.g. `walletSigningUnavailable`) to the guest-
 * facing i18n key. Reason codes are operator vocabulary and never reach the
 * guest verbatim; only the per-kind "currently unavailable" line does, except
 * `guestUnavailable` which means the person itself is not (or no longer)
 * eligible.
 */
export function mapTicketDropReason(
  kind: TicketDropKind,
  reason: string | undefined | null,
): TicketUnavailableKey {
  if (reason === "guestUnavailable" || reason === "notEligible") return "ineligible";
  switch (kind) {
    case "apple":
      return "unavailableApple";
    case "google":
      return "unavailableGoogle";
    default:
      return "unavailablePdf";
  }
}

/**
 * Both scan-token halves are validated so a malformed value never reaches the
 * QR encoder (the door scanner enforces exactly this shape:
 * 24 hex + "." + 16 base64url chars).
 */
export function isScanToken(value: unknown): value is string {
  return typeof value === "string" && /^[0-9a-fA-F]{24}\.[A-Za-z0-9_-]{16}$/.test(value);
}
